
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
// import { loadStripe } from '@stripe/stripe-js';

// Mock Stripe Load (or real if key provided later)
// const stripePromise = loadStripe('pk_test_...');

interface Product {
    _id: string;
    sku: string;
    name: string;
    description: string;
    priceEUR: number;
    currencyAmount?: number;
    type: 'CURRENCY' | 'CONTENT';
}

export const ShopPage: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const res = await api.get('/payment/products');
            setProducts(res.data);
        } catch (error) {
            toast.error('Impossible de charger la boutique');
        } finally {
            setLoading(false);
        }
    };

    const handleBuy = async (product: Product) => {
        try {
            setPurchasing(product.sku);
            const res = await api.post('/payment/checkout', { sku: product.sku });

            if (res.data.url) {
                // Redirect to Strpe (or Mock success page)
                window.location.href = res.data.url;
            } else {
                toast.error('Erreur lors de l\'initiation du paiement');
            }
        } catch (error) {
            toast.error('Erreur technique');
            setPurchasing(null);
        }
    };

    return (
        <div className="min-h-screen bg-navy-950 text-cream-100 p-8 font-serif">
            <header className="mb-12 text-center">
                <h1 className="text-4xl md:text-5xl font-bold text-gold-500 mb-4 drop-shadow-lg">
                    L'Échoppe aux Souvenirs
                </h1>
                <p className="text-navy-300 max-w-2xl mx-auto italic border-l-4 border-gold-500 pl-4 py-2 bg-navy-900/50 rounded-r-lg">
                    "Ici, le temps s'achète et les souvenirs se collectionnent.
                    Échangez quelques pièces contre des fragments d'éternité..."
                </p>
            </header>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="h-16 w-16 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {products.map((product) => (
                        <motion.div
                            key={product._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-navy-900 border-2 border-navy-800 rounded-xl overflow-hidden hover:border-gold-500/50 transition-all shadow-xl group relative"
                        >
                            {/* Decorative Background Glow */}
                            <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                            <div className="p-8 flex flex-col items-center text-center relative z-10">
                                <div className="w-24 h-24 bg-navy-950 rounded-full flex items-center justify-center mb-6 border-2 border-gold-500/30 group-hover:scale-110 transition-transform shadow-inner-gold">
                                    <span className="text-4xl">
                                        {product.type === 'CURRENCY' ? '🪙' : '📚'}
                                    </span>
                                </div>

                                <h3 className="text-2xl font-bold text-cream-50 mb-2">{product.name}</h3>
                                <p className="text-navy-300 mb-6 text-sm min-h-[40px]">{product.description}</p>

                                <div className="mt-auto w-full">
                                    <div className="text-3xl font-bold text-gold-400 mb-4 font-mono">
                                        {(product.priceEUR / 100).toFixed(2)} €
                                    </div>

                                    <button
                                        onClick={() => handleBuy(product)}
                                        disabled={!!purchasing}
                                        className="w-full btn-vintage py-3 text-lg font-bold uppercase tracking-widest hover:shadow-gold-glow disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ '--btn-bg': '#d4af37' } as any}
                                    >
                                        {purchasing === product.sku ? (
                                            <span className="animate-pulse">Traitement...</span>
                                        ) : (
                                            'Acquérir'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            <footer className="mt-16 text-center text-navy-500 text-xs">
                <p>Paiements sécurisés par Stripe. Les transactions apparaissent sous le nom "REMINISCENCE GAME".</p>
                <div className="mt-4 flex justify-center gap-4">
                    <a href="#" className="hover:text-gold-400">Conditions Générales de Vente</a>
                    <span>•</span>
                    <a href="#" className="hover:text-gold-400">Politique de Confidentialité</a>
                </div>
            </footer>
        </div>
    );
};
